// apps/web/src/components/platform/ManagerPage.tsx
//
// Reusable page scaffold for the platform manager screens. Standardizes
// the loading / error / empty states and the header + create-button
// pattern that every manager uses, so individual managers stay small.

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { apiClient } from '../../lib/apiClient';

interface ManagerPageProps {
  title: string;
  /** API path used for the initial list fetch. e.g. '/api/v1/issues' */
  listPath: string;
  /** Key of the array in the API response. e.g. 'issues' */
  listKey: string;
  /** Label for the primary create action, e.g. 'Create Issue' */
  createLabel?: string;
  renderCreateForm?: (onCancel: () => void) => ReactNode;
  renderItem: (item: unknown, index: number) => ReactNode;
  className?: string;
}

export function ManagerPage({
  title,
  listPath,
  listKey,
  createLabel,
  renderCreateForm,
  renderItem,
  className,
}: ManagerPageProps): JSX.Element {
  const [items, setItems] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    return apiClient
      .get(listPath)
      .then((res) => {
        const data = (res.data as Record<string, unknown>)[listKey];
        setItems(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        setError((err as { message?: string })?.message ?? 'Failed to load.');
        setItems([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listPath]);

  return (
    <div className={`manager-page ${className ?? ''}`}>
      <div className="header">
        <h2>{title}</h2>
        {createLabel && (
          <button type="button" className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? 'Cancel' : createLabel}
          </button>
        )}
      </div>

      {showCreate && renderCreateForm && (
        <div className="manager-create">
          {renderCreateForm(() => {
            setShowCreate(false);
            void load();
          })}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading…</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          No {title.toLowerCase()} yet.
          {createLabel ? ' Use the button above to create one.' : ''}
        </div>
      ) : (
        <div className="manager-grid">{items.map(renderItem)}</div>
      )}
    </div>
  );
}